import { useState } from "react"

import {
  AskQuestionEvalMock,
  deleteAiEvalMock,
  deleteManyAiEvalMocks,
  listAiEvalMocks
} from "@domain/ai_eval_mocks"
import AddIcon from "@mui/icons-material/Add"
import BiotechIcon from "@mui/icons-material/Biotech"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"

import { ImportJsonDialog } from "./sub_components/ImportJsonDialog"
import { MockFormDialog } from "./sub_components/MockFormDialog"

const MOCKS_QUERY_KEY = "ai-eval-mocks"

export default function AiEvalMocksPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AskQuestionEvalMock | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const mocksQuery = useQuery({
    queryKey: [MOCKS_QUERY_KEY, search],
    queryFn: () => listAiEvalMocks({ search: search || undefined, limit: 100 })
  })

  const deleteMut = useMutation({
    mutationFn: deleteAiEvalMock,
    onSuccess: (res) => {
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || "Silinemedi")
        return
      }
      showSuccessToast("Senaryo silindi")
      queryClient.invalidateQueries({ queryKey: [MOCKS_QUERY_KEY] })
    }
  })

  const docs = mocksQuery.data?.data?.docs || []
  const allIds = docs.map((d) => d._id)
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
  const someSelected = allIds.some((id) => selectedIds.has(id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(allIds))
    }
  }

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    if (
      !window.confirm(
        `${ids.length} senaryo silinsin mi? Bu işlem geri alınamaz.`
      )
    )
      return
    setBulkDeleting(true)
    try {
      const res = await deleteManyAiEvalMocks(ids)
      if (res.isSuccess) {
        showSuccessToast(`${res.data ?? ids.length} senaryo silindi`)
        setSelectedIds(new Set())
        queryClient.invalidateQueries({ queryKey: [MOCKS_QUERY_KEY] })
      } else {
        showErrorToast("Silinemedi")
      }
    } finally {
      setBulkDeleting(false)
    }
  }

  const handleCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const handleEdit = (mock: AskQuestionEvalMock) => {
    setEditing(mock)
    setDialogOpen(true)
  }

  const handleDelete = (mock: AskQuestionEvalMock) => {
    if (!window.confirm(`"${mock.title}" senaryosu silinsin mi?`)) return
    deleteMut.mutate(mock._id)
  }

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <BiotechIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              AI Eval Test Senaryoları
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Eval testleri için senaryolar oluştur ve yönet
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Senaryo ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              )
            }}
          />
          {selectedIds.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              disableElevation
            >
              {bulkDeleting
                ? "Siliniyor..."
                : `Seçilenleri Sil (${selectedIds.size})`}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => setImportDialogOpen(true)}
            disableElevation
          >
            JSON ile İçe Aktar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disableElevation
          >
            Yeni Senaryo
          </Button>
        </Stack>
      </Stack>

      {/* Table */}
      <Paper
        variant="outlined"
        sx={{ flex: 1, overflow: "auto", borderRadius: 2 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ bgcolor: "background.default", width: 48 }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, bgcolor: "background.default" }}
              >
                Senaryo
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: "background.default",
                  width: 120
                }}
              >
                Kanal
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, bgcolor: "background.default" }}
              >
                Cases
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  bgcolor: "background.default",
                  width: 80
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {mocksQuery.isLoading && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  Yükleniyor...
                </TableCell>
              </TableRow>
            )}

            {!mocksQuery.isLoading && docs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Stack
                    alignItems="center"
                    spacing={1}
                    sx={{ py: 6, opacity: 0.5 }}
                  >
                    <BiotechIcon sx={{ fontSize: 40 }} />
                    <Typography variant="body2" color="text.secondary">
                      {search
                        ? `"${search}" için senaryo bulunamadı`
                        : 'Henüz senaryo yok — "Yeni Senaryo" ile oluştur'}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {docs.map((mock) => (
              <TableRow
                key={mock._id}
                hover
                selected={selectedIds.has(mock._id)}
                sx={{ "& td": { verticalAlign: "middle" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(mock._id)}
                    onChange={() => toggleOne(mock._id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {mock.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    fontFamily="monospace"
                  >
                    {mock.id}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {mock.channelType || "—"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Tooltip
                    placement="top-start"
                    arrow
                    title={
                      <Box sx={{ whiteSpace: "pre-line" }}>
                        {(mock.cases || [])
                          .map(
                            (c, i) =>
                              `${i + 1}. ${c.query}${
                                c.assertions?.expectedAiAction
                                  ? ` [${c.assertions.expectedAiAction}]`
                                  : ""
                              }${
                                c.assertions?.expectedContentIncludes?.length
                                  ? ` → "${c.assertions.expectedContentIncludes.join(", ")}"`
                                  : ""
                              }`
                          )
                          .join("\n") || "—"}
                      </Box>
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ cursor: "default", maxWidth: 380 }}
                    >
                      <Chip
                        size="small"
                        label={`${(mock.cases || []).length} case`}
                        variant="outlined"
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {(mock.cases || [])[0]?.query || "—"}
                      </Typography>
                    </Stack>
                  </Tooltip>
                </TableCell>

                <TableCell align="right">
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={0.5}
                  >
                    <Tooltip title="Düzenle">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(mock)}
                        sx={{
                          color: "text.secondary",
                          ":hover": { color: "primary.main" }
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(mock)}
                        disabled={deleteMut.isPending}
                        sx={{
                          color: "text.secondary",
                          ":hover": { color: "error.main" }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <MockFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false)
          queryClient.invalidateQueries({ queryKey: [MOCKS_QUERY_KEY] })
        }}
        initial={editing}
      />

      <ImportJsonDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImported={() =>
          queryClient.invalidateQueries({ queryKey: [MOCKS_QUERY_KEY] })
        }
      />
    </Box>
  )
}
